import { clsx } from 'clsx';
import {
  type ReactNode,
  type ComponentPropsWithoutRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { usePopover } from '@repo/ui/Popover/PopoverRoot';
import {
  positionPopover,
  computeArrowOffset,
  type Alignment,
  type Placement,
  type Offset,
} from '@repo/lib/position';
import { debounce } from '@repo/lib/functions';

import s from './Popover.module.css';

export const variantOptions = ['dark', 'light'] as const;
export type Variant = (typeof variantOptions)[number];

export type PopoverProps = ComponentPropsWithoutRef<'div'> & {
  align?: Alignment;
  ariaLabel?: string;
  className?: string;
  children?: ReactNode;
  hasArrow?: boolean;
  offset?: Offset;
  placement?: Placement;
  variant?: Variant;
  viewportMargin?: number;
};

export const Popover = (props: PopoverProps) => {
  const {
    align = 'center',
    ariaLabel = 'More information',
    children,
    className,
    hasArrow = true,
    offset = 2,
    placement = 'top',
    variant = 'dark',
    viewportMargin = 2,
    ...attrs
  } = props;
  const { close, id, isOpen, open, popoverRef, triggerRef } = usePopover();
  const arrowRef = useRef<HTMLSpanElement>(null);

  const handleResize = useCallback(() => {
    if (popoverRef.current) {
      popoverRef.current.hidePopover();
      close();
    }
  }, [popoverRef, close]);

  const debouncedHandleResize = useMemo(
    () => debounce(handleResize, 150),
    [handleResize],
  );

  const calculateAndApplyPosition = useCallback(() => {
    if (!popoverRef.current || !triggerRef.current) return;

    const popover = popoverRef.current;
    const triggerRect = triggerRef.current.getBoundingClientRect();

    // Apply data attributes first
    popover.dataset.align = align;
    popover.dataset.placement = placement;

    const popoverRect = popover.getBoundingClientRect();

    const { position, flip } = positionPopover(
      triggerRect,
      popoverRect,
      placement,
      align,
      { offset, viewportMargin },
    );

    // Apply position
    popover.style.top = `${position.y}px`;
    popover.style.left = `${position.x}px`;

    // Update placement if it was flipped
    if (flip.placement) {
      popover.dataset.placement = flip.placement;
    }

    // Compute arrow position after final placement
    if (hasArrow) {
      const finalPlacement = flip.placement ?? placement;

      const arrowOffset = computeArrowOffset({
        triggerRect,
        popoverRect,
        position,
        finalPlacement,
      });

      const arrowEl = arrowRef.current;
      if (arrowEl) {
        // Reset both before applying
        arrowEl.style.left = '';
        arrowEl.style.top = '';
        if (finalPlacement === 'top' || finalPlacement === 'bottom') {
          arrowEl.style.left = `${arrowOffset}px`;
        } else {
          arrowEl.style.top = `${arrowOffset}px`;
        }
      }
    }
  }, [
    popoverRef,
    triggerRef,
    placement,
    align,
    offset,
    viewportMargin,
    hasArrow,
  ]);

  const handleToggle = useCallback(
    (event: ToggleEvent) => {
      if (event.newState === 'open') {
        calculateAndApplyPosition();
        open();
      } else {
        close();
      }
    },
    [calculateAndApplyPosition, open, close],
  );

  useEffect(() => {
    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [debouncedHandleResize]);

  useLayoutEffect(() => {
    const popover = popoverRef.current;
    if (!popover) return;

    popover.addEventListener('toggle', handleToggle as EventListener);

    return () => {
      popover.removeEventListener('toggle', handleToggle as EventListener);
    };
  }, [popoverRef, handleToggle]);

  return (
    <div
      aria-label={ariaLabel}
      aria-live={isOpen ? 'polite' : 'off'}
      aria-modal="false"
      className={s.popoverEl}
      id={id}
      popover="auto"
      role="dialog"
      ref={popoverRef}
      {...attrs}
    >
      <div
        style={{ visibility: isOpen ? 'visible' : 'hidden' }}
        className={clsx(s.popover, s[`${variant}Variant`], className)}
        data-has-arrow={hasArrow}
      >
        {hasArrow && (
          <span className={s.arrow} aria-hidden="true" ref={arrowRef} />
        )}
        {children}
      </div>
    </div>
  );
};
